import { Column, createRows, Image, Row, Table, TableColumn, Text } from "@bridgesplit/ui";
import { bsMath, formatPercent, formatUsd } from "@bridgesplit/utils";
import { LendingVaultExpanded, useOraclePrices, BsMetaUtil } from "@bridgesplit/abf-react";
import { NeighboringCurrencyDisplay } from "app/components/common";

import { AllocationSummarized } from "./type";
import { AllocationColumn } from "../params/type";

export default function AllocationTable({
    allocations,
    vaultExpanded,
    visibleColumns
}: {
    allocations: AllocationSummarized[] | undefined;
    vaultExpanded?: LendingVaultExpanded | undefined;
    visibleColumns: AllocationColumn[];
}) {
    const { getUsdPrice } = useOraclePrices(allocations?.map((a) => a.collateralMetadata?.mint) ?? []);
    const columns = getAllocationTableColumns(vaultExpanded, getUsdPrice);

    return (
        <Table
            fixTableHeight
            paginationProps={{
                pageSize: 5,
                type: "managed",
                defaultSortKey: "allocation"
            }}
            emptyPlaceholder="No active loans"
            loading={!allocations}
            rows={createRows(allocations, (s) => s.key)}
            columns={columns.filter((c) => visibleColumns.includes(c.title as AllocationColumn))}
        />
    );
}

function getAllocationTableColumns(
    vaultExpanded: LendingVaultExpanded | undefined,
    getUsdPrice: (mint: string | undefined) => number | undefined
): TableColumn<AllocationSummarized>[] {
    const metadata = vaultExpanded?.principalMetadata;

    return [
        {
            dataIndex: "source",
            title: AllocationColumn.Source,
            render: ({ data }) => (
                <Row spacing={1}>
                    <Image src={data.image} size="20px" variant="circle" />
                    <Text>{data.name}</Text>
                </Row>
            ),
            sortBy: ({ data }) => data.name
        },
        {
            dataIndex: "allocation",
            title: AllocationColumn.Allocation,
            align: "right",
            render: ({ data }) => (
                <Column alignItems="flex-end">
                    <NeighboringCurrencyDisplay metadata={metadata} amount={data.totalPrincipalDeployed} />
                    <Text color="caption" variant="body2">
                        {formatPercent(data.allocationPercent)}
                    </Text>
                </Column>
            ),
            sortBy: ({ data }) => data.totalPrincipalDeployed
        },
        {
            dataIndex: "collateralDepositedUsd",
            title: AllocationColumn.CollateralDeposits,
            align: "right",
            render: ({ data }) => <CollateralDeposits data={data} getUsdPrice={getUsdPrice} />,
            sortBy: ({ data }) => data.collateralDepositedUsd ?? 0
        },
        {
            dataIndex: "apy",
            title: AllocationColumn.Apy,
            align: "right",
            render: ({ data }) => (
                <Column alignItems="flex-end">
                    <Text> {formatPercent(data.wAvgApy)} </Text>
                    <Text color="caption" variant="body2">
                        {data.isIdle ? "Variable rate" : `${data.activeLoanCount} loans`}
                    </Text>
                </Column>
            ),
            sortBy: ({ data }) => data.wAvgApy
        }
    ];
}

function CollateralDeposits({
    data,
    getUsdPrice
}: {
    data: AllocationSummarized;
    getUsdPrice: (mint: string | undefined) => number | undefined;
}) {
    const collateralAmountOfDeposits = bsMath.div(
        data?.collateralDepositedUsd ?? 0,
        getUsdPrice(data.collateralMetadata?.mint)
    );

    return (
        <Column alignItems="flex-end">
            <Text>
                {BsMetaUtil.formatAmount(data.collateralMetadata, collateralAmountOfDeposits, {
                    hideSymbol: true,
                    decimals: data.collateralMetadata?.decimals
                })}
            </Text>
            <Text color="caption" variant="body2">
                {formatUsd(data.collateralDepositedUsd ?? 0)}
            </Text>
        </Column>
    );
}
